import { Button } from '../../../components/ui/button'
// @ts-strict-ignore
import React from 'react'
import { NumericInput } from '@blueprintjs/core'
import { HTMLSelect } from '../../../components/ui/html-select'
import { NumericFilter, NumericFilterValue, Operator } from '../../../constants/filter'

// Value is what will be used internally
// Label is what will be displayed to the user
interface OperatorSelect {
    value: Operator
    label: string
}

const defaultOperators: OperatorSelect[] = [
    { value: Operator.EqualTo, label: '=' },
    { value: Operator.NotEqualTo, label: '!=' },
    { value: Operator.GreaterThan, label: '>' },
    { value: Operator.GreaterThanOrEqualTo, label: '>=' },
    { value: Operator.LessThan, label: '<' },
    { value: Operator.LessThanOrEqualTo, label: '<=' },
    { value: Operator.IsNull, label: 'is null' },
]

const allowedOperators: OperatorSelect[] = [
    ...defaultOperators,
    { value: Operator.Between, label: 'between' },
]

type FilterValueObject = {
    [Operator.EqualTo]?: number
    [Operator.NotEqualTo]?: number
    [Operator.GreaterThan]?: number
    [Operator.GreaterThanOrEqualTo]?: number
    [Operator.LessThan]?: number
    [Operator.LessThanOrEqualTo]?: number
    [Operator.IsNull]?: number
}

interface FilterNumberProps {
    filter: NumericFilter
    value: NumericFilterValue
    nullable: boolean
    onChange: (FilterValue) => any
    apply?: () => any
}

export const FilterNumber = ({ filter, value, nullable, onChange, apply }: FilterNumberProps) => {
    const operators = filter.operators
        ? allowedOperators.filter((o) => filter.operators.indexOf(o.value) !== -1)
        : defaultOperators

    console.log('Filter number value is', value)

    const handleTypeChange = () => {
        return (e) => {
            const type = e.target.value
            const v = getValue(value) as number

            let payload: NumericFilterValue
            if (type === Operator.EqualTo) {
                payload = v as number
            } else if (type === Operator.Between) {
                payload = {
                    [Operator.GreaterThanOrEqualTo]: v.toString(),
                    [Operator.LessThanOrEqualTo]: v.toString(),
                }
            } else {
                payload = {
                    [type]: v,
                }
            }

            onChange(payload)
        }
    }

    const type = (): Operator => {
        let v: Operator = resolveType(value)

        if (typeof v === 'undefined') {
            // @todo resolve default
            v = Operator.EqualTo
        }

        return v
    }

    const resolveType = (item: NumericFilterValue): Operator => {
        if (typeof item === 'object') {
            const keys = Object.keys(item)

            if (keys.length > 1) {
                return Operator.Between
            } else {
                return Object.keys(item)[0] as Operator
            }
        }

        return Operator.EqualTo
    }

    const handleDirty = () => {
        return (v: NumericFilterValue) => {
            const resolvedOperator: Operator = type()

            let payload
            if (resolvedOperator === Operator.EqualTo || resolvedOperator === Operator.Between) {
                payload = v
            } else {
                payload = {
                    [resolvedOperator]: v,
                }
            }

            onChange(payload)
        }
    }

    const handleKeyDown = (e: KeyboardEvent): void => {
        if (e.key === 'Enter' && apply) {
            apply()
        }
    }

    const resolvedType: Operator = type()
    return (
        <div className="mt-2 flex items-center gap-2">
            <HTMLSelect options={operators} onChange={handleTypeChange()} value={resolvedType} />
            <FilterNumberInput
                resolvedType={resolvedType}
                value={value}
                onValueChange={handleDirty()}
                prefix={filter.prefix}
                suffix={filter.suffix}
                onKeyDown={handleKeyDown}
            />
        </div>
    )
}

interface FilterNumberInputProps {
    resolvedType: Operator
    value: NumericFilterValue
    onValueChange: (FilterValue) => any
    prefix?: string
    suffix?: string
    onKeyDown?: (KeyboardEvent) => any
}

const FilterNumberInput = ({
    resolvedType,
    value,
    onValueChange,
    prefix,
    suffix,
    onKeyDown,
}: FilterNumberInputProps): JSX.Element | null => {
    if (resolvedType === Operator.IsNull) {
        return null
    }

    const wrap = (input: JSX.Element) => (
        <div className="flex items-center gap-1">
            {prefix ? <span className="text-gray-500 text-sm">{prefix}</span> : null}
            {input}
            {suffix ? <span className="text-gray-500 text-sm">{suffix}</span> : null}
        </div>
    )

    if (resolvedType === Operator.Between) {
        const resolvePayload = (type: Operator) => (v: string) => {
            onValueChange({
                ...(value as FilterValueObject),
                [type]: v,
            })
        }

        return (
            <div className="flex items-center">
                {wrap(
                    <NumericInput
                        style={{ width: '80px' }}
                        buttonPosition="none"
                        onKeyDown={(e) => (onKeyDown ? onKeyDown(e) : null)}
                        value={value['>=']}
                        onValueChange={(_, v) => resolvePayload(Operator.GreaterThanOrEqualTo)(v)}
                    />,
                )}
                <span className="mx-1">and</span>
                {wrap(
                    <NumericInput
                        style={{ width: '80px' }}
                        onKeyDown={(e) => (onKeyDown ? onKeyDown(e) : null)}
                        buttonPosition="none"
                        value={value['<=']}
                        onValueChange={(_, v) => resolvePayload(Operator.LessThanOrEqualTo)(v)}
                    />,
                )}
            </div>
        )
    }

    return wrap(
        <NumericInput
            stepSize={0.01}
            onKeyDown={(e) => (onKeyDown ? onKeyDown(e) : null)}
            minorStepSize={null}
            buttonPosition="none"
            fill
            value={getValue(value)}
            onValueChange={(_, v) => onValueChange(v)}
        />,
    )
}

const getValue = (value: NumericFilterValue): number | string => {
    let v: string | number = resolveValue(value)

    if (typeof v === 'undefined') {
        v = ''
    }

    return v
}

const resolveValue = (item: NumericFilterValue): number => {
    if (typeof item === 'object') {
        return item[Object.keys(item)[0]]
    }

    return item
}
